import {
	getNavigationDisplayName,
	getNavigationPath,
	NavigationEntityType,
} from '@AppModels/hooks/useAINavigationResolver';
import { useQueryClient } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { Draft, produce } from 'immer';
import React, { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useStreamingResponseMutation } from '../../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { searchRequestToQueryStringParams } from '../../../../models/UiUtils';
import { useAdminAccountQuery } from '../../../../queries';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	useAIConversationFilterQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { FullScreenModalViewModel } from '../../../../viewmodels/AppViewModels';
import WorkingLionImageUrl from '../../../assets/aiAssistantThinking.png';
import { AIMarkdownResponse } from '../../../components-v2/ai/AiMarkdownResponse';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../components-v2/ai/ChatConversation';
import { Button } from '../../../components-v2/Button';
import { Avatar2 } from '../../../components/Avatar2';
import { CloseButton } from '../../../components/CloseButton';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ChevronRight } from '../../../components/svgs/icons/ChevronRight';
import { PlusIcon } from '../../../components/svgs/icons/PlusIcon';
import { SparkleIcon } from '../../../components/svgs/icons/SparkleIcon';
import { SparklesIcon } from '../../../components/svgs/icons/SparklesIcon';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { ROUTES } from '../../../constants/routes';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { AssistantChatContactCard } from './AssistantChatContactCard';
import { s } from './styles';

const GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT = `GoogleBusinessReviewReplyAssistant`;
const UNIVERSAL_ASSISTANT_NAME = `SiteWideAgent`;
const ADMIN_AGENT_NAME = `AdminAgent`;
const SUCCESS_MEETING_PREP_AGENT_NAME = `SuccessMeetingPrepAgent`;

interface PartialToolCall extends Api.AIToolCall {
	partial: Record<string, string>;
}

// FE only type for handling error messages in chat
interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}

function CampaignComposerAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.chatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								className={css(s.plusIconButton)}
								style={onClose ? { marginRight: '24px' } : undefined}
								onClick={onNewChat}
							>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

const useAssistantConversation = (
	assistantName: string,
	isOpen: boolean,
	impersonationContext?: Api.IImpersonationContext
) => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useAssistantConversation');
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`useAssistantConversation-${assistantName}`, error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiAssistantCreateConversationMutation.data),
		id: aiAssistantCreateConversationMutation.data?.id,
		impersonationContext,
	});
	useEffect(() => {
		if (isOpen && aiAssistantCreateConversationMutation.isIdle && !aiAssistantCreateConversationMutation.data) {
			aiAssistantCreateConversationMutation.mutate({
				assistantName,
				impersonationContext,
			});
		}
	}, [aiAssistantCreateConversationMutation, isOpen, assistantName, impersonationContext]);
	return {
		conversationId: aiAssistantCreateConversationMutation.data?.id,
		aiConversationChatMessageQuery,
	};
};

export function AIChatAssistantMessage({ children }: { children: ReactNode }) {
	return (
		<div className={css(s.assitantMessage)}>
			<SparkleIcon styles={[s.assistantMessageSparkle]} />
			<div className={css(s.assistantMessageContent)}>{children}</div>
		</div>
	);
}

function emailToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { subject: string; body: string };
	if (isEmailToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function socialToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { content: string };
	if (isSocialToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function googleReviewToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { reply: string };
	if (isGoogleReviewReplyToolCall(toolCall)) {
		content = { reply: toolCall.reply };
	} else if (toolCall.partial) {
		content = { reply: (toolCall.partial as { body: string }).body };
	}
	return content;
}

function blogToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { title: string; body: string };
	if (isBlogToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

export function AIChatUserMessage({ children }: { children: ReactNode }) {
	return <div className={css(s.userMessage)}>{children}</div>;
}

function isAIChatStreamToolCallResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamToolResponse {
	return Boolean((sseData as Api.AIChatStreamToolResponse)?.toolCall);
}

function isAIChatStreamTextResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamMessageResponse {
	return Boolean((sseData as Api.AIChatStreamMessageResponse)?.message);
}

function isEmailToolCall(toolCall: Api.AIToolCall): toolCall is Api.EmailToolCall {
	return Boolean((toolCall as Api.EmailToolCall)?.body);
}

function isSocialToolCall(toolCall: Api.AIToolCall): toolCall is Api.SocialMediaToolCall {
	return Boolean((toolCall as Api.SocialMediaToolCall)?.content);
}

function isGoogleReviewReplyToolCall(toolCall: Api.AIToolCall): toolCall is Api.GoogleReviewReplyToolCall {
	return Boolean((toolCall as Api.GoogleReviewReplyToolCall)?.reply);
}

function isBlogToolCall(toolCall: Api.AIToolCall): toolCall is Api.BlogToolCall {
	return Boolean((toolCall as Api.BlogToolCall)?.title);
}

function isContactListToolCall(toolCall: Api.AIToolCall): toolCall is Api.ContactListToolCall {
	return Boolean((toolCall as Api.ContactListToolCall)?.toolName === 'lev-contacts');
}

function isNavigationToolCall(toolCall: Api.AIToolCall): toolCall is Api.NavigationToolCall {
	return Boolean((toolCall as Api.NavigationToolCall)?.toolName === 'lev-navigation');
}

// Not used now. POC for contact tool call. Keeping in case we add contact tool calls soon.
/* function isContactToolCall(toolCall: Api.AIToolCall): toolCall is Api.ContactToolCall {
	return Boolean((toolCall as Api.ContactToolCall)?.contactId);
} */
export const useAIAssistantStreamingMutation = ({
	assistantName,
}: {
	assistantName: string;
	onError?: (error: Error) => void;
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const queryClient = useQueryClient();
	const logger = useEventLogging('useAIAssistantStreamingMutation');
	const assistantStreamingMutation = useStreamingResponseMutation({
		mutationFn: (params: {
			conversationId: string;
			chatRequest: Api.AIChatRequest;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			new Promise<Response>((resolve, reject) =>
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${params.conversationId}/stream`,
						impersonationContext: params.impersonationContext,
					}),
					'POST',
					params.chatRequest,
					resolve,
					reject
				)
			),
		onEvent: (event, params) => {
			const rqConversationQueryKey = getConversationChatMessageQueryKey({
				id: params.conversationId,
				impersonationContext: params.impersonationContext,
			});
			if (event.event === 'Error') {
				logger.logEvent('AssistantChatStreamEvent-Error', {
					message: event.data,
				});
				queryClient.setQueryData(rqConversationQueryKey, (messages: Api.AIChatMessage[]) => {
					const errorMessage: AIChatMessageError = {
						id: crypto.randomUUID(),
						error: event.data,
						chatRequest: params.chatRequest,
					};
					return [...messages, errorMessage];
				});
				return;
			}
			if (event.event !== 'Response' && event.event !== 'ToolResponse') {
				return;
			}
			const aiChatStreamMessageResponse = JSON.parse(event.data) as Api.AIChatStreamResponse;
			if (isAIChatStreamTextResponse(aiChatStreamMessageResponse)) {
				queryClient.setQueryData(rqConversationQueryKey, (messages: Api.AIChatMessage[]) => {
					const aiTextMessage: Api.AITextContent = {
						_type: 'AITextContent',
						text: aiChatStreamMessageResponse.message.text,
					};
					return produce(messages ?? [], draftMessages => {
						const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
						if (currentMessage?.role !== Api.AIChatRole.Assistant) {
							draftMessages.push({
								id: aiChatStreamMessageResponse.id,
								role: Api.AIChatRole.Assistant,
								contents: [aiTextMessage],
							});
							return draftMessages;
						}
						const lastMessageContent = currentMessage.contents[
							currentMessage.contents.length - 1
						] as Draft<Api.AITextContent>;
						if (lastMessageContent._type !== aiTextMessage._type) {
							currentMessage.contents.push(aiTextMessage);
							return draftMessages;
						}
						if (!aiChatStreamMessageResponse.isDelta) {
							currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiTextMessage);
							return draftMessages;
						}
						lastMessageContent.text += aiTextMessage.text;
						return draftMessages;
					});
				});
			}
			if (
				isAIChatStreamToolCallResponse(aiChatStreamMessageResponse) &&
				(aiChatStreamMessageResponse.toolCall.toolName === 'lev-contact' ||
					aiChatStreamMessageResponse.toolCall.toolName === 'lev-contacts' ||
					aiChatStreamMessageResponse.toolCall.toolName === 'lev-navigation')
			) {
				if (aiChatStreamMessageResponse.isDelta) {
					return;
				}
				queryClient.setQueryData(rqConversationQueryKey, (messages: Api.AIChatMessage[]) => {
					const aiToolCallContent: Api.AIToolContent = {
						_type: 'AIToolContent',
						toolCall: aiChatStreamMessageResponse.toolCall,
					};
					return produce(messages ?? [], draftMessages => {
						const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
						if (currentMessage?.role !== Api.AIChatRole.Assistant) {
							draftMessages.push({
								id: aiChatStreamMessageResponse.id,
								role: Api.AIChatRole.Assistant,
								contents: [aiToolCallContent],
							});
							return draftMessages;
						}
						currentMessage.contents.push(aiToolCallContent);
						return draftMessages;
					});
				});
				return;
			}
			if (isAIChatStreamToolCallResponse(aiChatStreamMessageResponse)) {
				if (aiChatStreamMessageResponse.isDelta && !aiChatStreamMessageResponse.toolCall?.propertyName) {
					return;
				}
				queryClient.setQueryData(rqConversationQueryKey, (messages: Api.AIChatMessage[]) => {
					const aiToolCallContent: Api.AIToolContent = {
						_type: 'AIToolContent',
						toolCall: {
							toolName: aiChatStreamMessageResponse.toolCall.toolName,
							value: '',
						} as PartialToolCall,
					};
					if (aiChatStreamMessageResponse.isDelta) {
						(aiToolCallContent.toolCall as PartialToolCall).partial = {
							[aiChatStreamMessageResponse.toolCall.propertyName]: aiChatStreamMessageResponse.toolCall.value,
						};
					} else {
						aiToolCallContent.toolCall = aiChatStreamMessageResponse.toolCall;
					}
					return produce(messages ?? [], draftMessages => {
						const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
						if (currentMessage?.role !== Api.AIChatRole.Assistant) {
							draftMessages.push({
								id: aiChatStreamMessageResponse.id,
								role: Api.AIChatRole.Assistant,
								contents: [aiToolCallContent],
							});
							return draftMessages;
						}
						const lastMessageContent = currentMessage.contents[
							currentMessage.contents.length - 1
						] as Draft<Api.AIToolContent>;
						if (
							lastMessageContent._type !== aiToolCallContent._type ||
							!(lastMessageContent.toolCall as PartialToolCall)?.partial
						) {
							currentMessage.contents.push(aiToolCallContent);
							return draftMessages;
						}
						if (!aiChatStreamMessageResponse.isDelta) {
							currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiToolCallContent);
							return draftMessages;
						}
						(lastMessageContent.toolCall as PartialToolCall).partial[
							aiChatStreamMessageResponse.toolCall.propertyName
						] =
							((lastMessageContent.toolCall as PartialToolCall).partial[
								aiChatStreamMessageResponse.toolCall.propertyName
							] ?? '') + aiChatStreamMessageResponse.toolCall.value;
						return draftMessages;
					});
				});
			}
		},
		onError: error => {
			console.error(error);
			logger.logApiError('AssistantChatStreamRequest-Error', error as Api.IOperationResultNoValue);
			errorMessages.pushApiError(error, 'Error while streaming AI assistant response');
		},
	});
	return assistantStreamingMutation;
};

function AssistantChatInput({
	placeholder = 'Ask me anything...',
	onSubmit,
	streamingInProgress = false,
}: {
	placeholder?: string;
	onSubmit: (prompt: string) => void;
	streamingInProgress: boolean;
}) {
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey && !ev.nativeEvent.isComposing) {
			ev.preventDefault();
			const prompt = textAreaRef.current.value.trim();
			if (prompt === '') {
				return;
			}
			if (streamingInProgress) {
				toast.error('Please wait for the current response to finish.');
				return;
			}
			onSubmit(prompt);
			textAreaRef.current.value = '';
		}
	};
	return (
		<textarea
			ref={textAreaRef}
			placeholder={placeholder}
			onKeyPress={handlePromptKeyPress}
			className={css(s.promptInput)}
		/>
	);
}

export function removeErrorMessageFromConversationCache(
	conversationId: string,
	impersonationContext?: Api.IImpersonationContext
) {
	const conversationQueryKey = getConversationChatMessageQueryKey({
		id: conversationId,
		impersonationContext,
	});
	const messages: Api.AIChatMessage[] = reactQueryClient.getQueryData(conversationQueryKey) ?? [];
	if ((messages.at(-1) as AIChatMessageError)?.error) {
		reactQueryClient.setQueryData(conversationQueryKey, messages.slice(0, -1));
	}
}

export function AIChatAssistantMessageError({ onTryAgain }: { onTryAgain: () => void }) {
	return (
		<AIChatAssistantMessage>
			<div className={css(s.errorMessageContainer)}>
				<div>Error</div>
				<p className={css(s.errorMessageDetails)}>Something went wrong with the assistant. Please try again.</p>
				<Button variant='destructive' size='sm' onClick={onTryAgain} styles={[s.errorMessageButton]}>
					Try Again
				</Button>
			</div>
		</AIChatAssistantMessage>
	);
}

export function GoogleReviewReplyAssistantChat({
	isOpen,
	onUseGoogleReviewReply,
	context,
	impersonationContext,
	onClose,
}: {
	isOpen: boolean;
	onUseGoogleReviewReply: (reply: string) => void;
	context?: {
		review: Api.IGoogleBusinessReview;
		companyName: string;
		reply: string;
	};
	impersonationContext?: Api.IImpersonationContext;
	onClose?: () => void;
}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const { conversationId, aiConversationChatMessageQuery } = useAssistantConversation(
		GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
		isOpen,
		impersonationContext
	);
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: GOOGLE_BUSINESS_REVIEW_REPLY_ASSISTANT,
	});
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
				contexts: context
					? [
							{
								_type: 'GoogleReviewAIContext',
								reviewedCompanyName: context.companyName,
								review: context.review,
								reply: context.reply,
							} as Api.GoogleReviewAIContext,
						]
					: undefined,
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	const handleCraftReplyMessageClick = () => {
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				action: {
					_type: 'GoogleReviewReplyAction',
					reviewId: context.review.id,
				} as Api.GoogleReviewReplyAction,
				contexts: [
					{
						_type: 'GoogleReviewAIContext',
						reviewedCompanyName: context.companyName,
						review: context.review,
						reply: context.reply,
					} as Api.GoogleReviewAIContext,
				],
			} as Api.AIActionRequest,
			impersonationContext,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!conversationId) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	return (
		<CampaignComposerAssistantLayout onClose={onClose}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I can help you craft a response. Do you have any specific instructions for
						this message?
					</AIChatAssistantMessage>
					{context?.review &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming ? (
						<button className={css(bs.ctaButtonReverse, s.aiKitCta)} onClick={handleCraftReplyMessageClick}>
							<span>Craft Reply Message</span> <ChevronRight />
						</button>
					) : null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === Api.AIChatRole.User) {
							return (
								<AIChatUserMessage key={`google-review-message-${message.id ?? i}`}>{message.text}</AIChatUserMessage>
							);
						}
						if (message.role === Api.AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`google-review-assistant-message-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as Api.AITextContent;
											return (
												<AIMarkdownResponse key={`google-review-assistant-message${message?.id ?? i}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
										) {
											const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
											return (
												<div
													key={`google-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.reply ?? ''}</div>
													{isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply)}
														>
															Use this Response
														</Button>
													) : null}
												</div>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`google-review-assistant-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Add Google Review Reply Prompt'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

function UniversalAssistantLayout({
	onClose,
	onNewChat,
	children,
}: {
	onClose?: () => void;
	onNewChat?: () => void;
	children: ReactNode;
}) {
	return (
		<div className={css(s.universalChatWindow)}>
			{onClose ? <CloseButton styles={[s.closeBtn]} onClick={onClose} /> : null}
			<div className={css(s.chatWindowHeader)}>
				<div className={css(s.chatWindowHeaderStart)}>
					<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={s.leoAvatar} />
					<SparklesIcon />
					<span className={css(s.chatWindowHeaderTitle)}>Ask Lev</span>
				</div>
				{onNewChat ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<button className={css(s.plusIconButton)} onClick={onNewChat}>
								<PlusIcon fillColor={brandPrimary} />
							</button>
						</TooltipTrigger>
						<TooltipContent>New Chat</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			<div className={css(s.chatContent)}>{children}</div>
		</div>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState) => void;
	onUseGoogleReviewReply?: (reply: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	impersonationContext?: Api.IImpersonationContext;
}

export const AIAssistantStreamingMutationContext = createContext<ReturnType<
	typeof useAIAssistantStreamingMutation
> | null>(null);

export function AIAssistantStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: UNIVERSAL_ASSISTANT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function AdminAgentStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: ADMIN_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

export function SuccessMeetingPrepStreamingMutationProvider({ children }: { children: ReactNode }) {
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
	});
	return (
		<AIAssistantStreamingMutationContext.Provider value={aiAssistantStreamingMutation}>
			{children}
		</AIAssistantStreamingMutationContext.Provider>
	);
}

interface UniversalAssistantChatProps {
	onUseAIEmail: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseSocialContent: (content: Api.IRawRichTextContentState) => void;
	onUseGoogleReviewReply?: (reply: string) => void;
	onUseBlogContent: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseContact: (value: Api.AIContactReference) => void;
	onUseContactList: (contactList: Api.ContactListToolCall) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

export function UniversalAssistantChat({
	onUseAIEmail,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onUseContact,
	onUseContactList,
	onNavigate,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: UniversalAssistantChatProps) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('UniversalAssistantChat');

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SiteWide,
				},
			],
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: UNIVERSAL_ASSISTANT_NAME,
				impersonationContext,
			});
		}
	}, [
		aiConversationFilterQuery.isSuccess,
		aiConversationFilterQuery.data,
		createUniversalAssistantConversation,
		impersonationContext,
	]);

	const conversationId = aiConversationFilterQuery.data?.[0]?.id;

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
				impersonationContext,
			} as Api.AIMessageRequest,
		});
	};

	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === Api.AIChatRole.User) {
							return (
								<AIChatUserMessage key={`email-assistant-message-${message.id ?? i}`}>{message.text}</AIChatUserMessage>
							);
						}
						if (message.role === Api.AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`universal-assistant-message-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as Api.AITextContent;
											return (
												<AIMarkdownResponse key={`email-assistant-message${message?.id}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-email'
										) {
											const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
											return (
												<div key={`email-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div>{previewContent.subject ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isEmailToolCall(partialEmailToolCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseAIEmail(partialEmailToolCall.subject, partialEmailToolCall.body)}
														>
															Use this email
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-contact'
										) {
											const contactToolContent = (content as Api.AIToolContent)
												.toolCall as unknown as Api.AIContactReference & {
												contactId: string;
											};
											return (
												<AssistantChatContactCard
													key={`contact-assistant-message${message?.id}-${j}`}
													firstName={contactToolContent.firstName}
													lastName={contactToolContent.lastName}
													email={contactToolContent.primaryEmail}
													phone={contactToolContent.phoneNumbers?.[0]?.value}
													company={contactToolContent.companyName}
													profilePic={contactToolContent.profilePic}
													onClick={() => {
														onUseContact({
															...contactToolContent,
															id: contactToolContent.contactId,
														});
													}}
												/>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											isContactListToolCall((content as Api.AIToolContent).toolCall)
										) {
											const contactListToolCall = (content as Api.AIToolContent).toolCall as Api.ContactListToolCall;
											return (
												<Button
													key={`contact-list-assistant-message${message?.id}-${j}`}
													onClick={() => {
														onUseContactList(contactListToolCall);
													}}
												>
													Show all {contactListToolCall.totalCount} contacts
												</Button>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
										) {
											const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = socialToolCallPreviewContent(partialSocialCall);
											return (
												<div key={`social-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.content ?? '',
														}}
													/>
													{isSocialToolCall(partialSocialCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseSocialContent(partialSocialCall.content)}
														>
															Use this post
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
										) {
											const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
											return (
												<div
													key={`google-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.reply ?? ''}</div>
													{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
														<Button
															variant='primary'
															styles={[s.useEmailBtn]}
															onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply)}
														>
															Use this Response
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-blog'
										) {
											const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = blogToolCallPreviewContent(partialBlog);
											return (
												<div
													key={`ai-blog-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.title ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isBlogToolCall(partialBlog) ? (
														<Button
															variant='primary'
															styles={[s.useEmailBtn]}
															onClick={() => onUseBlogContent(partialBlog.title, partialBlog.content)}
														>
															Use this blog
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											isNavigationToolCall((content as Api.AIToolContent).toolCall)
										) {
											const navigationToolCall = (content as Api.AIToolContent).toolCall as Api.NavigationToolCall;

											return (
												<div key={`navigation-assistant-message${message?.id}-${j}`}>
													{navigationToolCall.navigationItems?.map((navigationItem, navIndex) => {
														const displayText = getNavigationDisplayName(
															navigationItem.entityType as NavigationEntityType
														);
														// Extract parent entity information from parentNavigationMetadata
														const parentEntityType = navigationItem.parentNavigationMetadata?.entityType;
														const parentEntityId = navigationItem.parentNavigationMetadata?.id;
														return (
															<Button
																key={`navigation-item-${message?.id}-${j}-${navIndex}`}
																onClick={() => {
																	onNavigate?.(
																		navigationItem.entityType,
																		navigationItem.id,
																		parentEntityType,
																		parentEntityId
																	);
																}}
																styles={[s.useEmailBtn]}
															>
																{displayText}
															</Button>
														);
													})}
													{(!navigationToolCall.navigationItems || navigationToolCall.navigationItems.length === 0) && (
														<div key={`navigation-empty-${message?.id}-${j}`}>No navigation items available</div>
													)}
												</div>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`email-assistant-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</UniversalAssistantLayout>
	);
}

export function UniversalAssistantChatWithinComposer({
	onUseAIEmail,
	onUseSocialContent,
	onUseBlogContent,
	onClose,
	renderConversationStarters,
	emailContext,
	socialContext,
	blogContext,
	isOpen,
	impersonationContext,
}: {
	onUseAIEmail?: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState) => void;
	onUseBlogContent?: (title: string, content: Api.IRawRichTextContentState) => void;
	onClose: () => void;
	emailContext?: { subject: string; body: Api.IRawRichTextContentState };
	socialContext?: { content: Api.IRawRichTextContentState };
	blogContext?: { title: string; content: Api.IRawRichTextContentState };
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
	isOpen: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const logger = useEventLogging('UniversalAssistantChat');
	const onUseAIEmailHandler = onUseAIEmail ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContentHandler = onUseSocialContent ?? defaultOnUseSocialContent(fullscreenModal);
	// Google revies are diabled because we don't have a path for it yet
	const onUseGoogleReviewReply: (reply: string) => void = undefined;
	const onUseBlogContentHandler = onUseBlogContent ?? defaultOnUseBlogContent(fullscreenModal);
	const onUseContact = (contactInfo: Api.AIContactReference) => {
		history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
	};
	const onUseContactList = (contactList: Api.ContactListToolCall) => {
		const searchParams = searchRequestToQueryStringParams(
			{
				filter: { operator: 0, criteria: contactList.filters },
			},
			{ sort: 'asc', sortBy: 'handle' }
		);
		history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
	};

	const aiConversationFilterQuery = useAIConversationFilterQuery({
		impersonationContext,
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SiteWide,
				},
			],
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
	});
	const createUniversalAssistantConversationMutation = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});

	const handleNewChatClick = () => {
		createUniversalAssistantConversationMutation.mutate({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);

	if (!aiAssistantStreamingMutation) {
		throw new Error('UniversalAssistantChatWithinComposer must be wrapped in AIAssistantStreamingMutationProvider');
	}

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			impersonationContext,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
				contexts: (() => {
					const contexts = [
						...(emailContext
							? [
									{
										_type: 'EmailAIContext',
										subject: emailContext.subject,
										body: emailContext.body,
									} as Api.EmailAIContext,
								]
							: []),
						...(socialContext
							? [
									{
										_type: 'SocialMediaAIContext',
										content: socialContext.content,
									} as Api.SocialMediaAIContext,
								]
							: []),
						...(blogContext
							? [
									{
										_type: 'BlogAIContext',
										title: blogContext.title,
										content: blogContext.content,
									} as Api.BlogAIContext,
								]
							: []),
					];
					return contexts.length > 0 ? contexts : undefined;
				})(),
			} as Api.AIMessageRequest,
		});
	};
	if (!isOpen) {
		return null;
	}
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</CampaignComposerAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<CampaignComposerAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>Hey {userSession.user.firstName}, how can I help you today?</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === Api.AIChatRole.User) {
							return (
								<AIChatUserMessage key={`email-assistant-message-${message.id ?? i}`}>{message.text}</AIChatUserMessage>
							);
						}
						if (message.role === Api.AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`universal-assistant-message-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as Api.AITextContent;
											return (
												<AIMarkdownResponse key={`email-assistant-message${message?.id}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-email'
										) {
											const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
											return (
												<div key={`email-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div>{previewContent.subject ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isEmailToolCall(partialEmailToolCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() =>
																onUseAIEmailHandler(partialEmailToolCall.subject, partialEmailToolCall.body)
															}
														>
															Use this email
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-contact'
										) {
											const contactToolContent = (content as Api.AIToolContent)
												.toolCall as unknown as Api.AIContactReference & {
												contactId: string;
											};
											return (
												<AssistantChatContactCard
													key={`contact-assistant-message${message?.id}-${j}`}
													firstName={contactToolContent.firstName}
													lastName={contactToolContent.lastName}
													email={contactToolContent.primaryEmail}
													phone={contactToolContent.phoneNumbers?.[0]?.value}
													company={contactToolContent.companyName}
													profilePic={contactToolContent.profilePic}
													onClick={() => {
														onUseContact({
															...contactToolContent,
															id: contactToolContent.contactId,
														});
													}}
												/>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											isContactListToolCall((content as Api.AIToolContent).toolCall)
										) {
											const contactListToolCall = (content as Api.AIToolContent).toolCall as Api.ContactListToolCall;
											return (
												<Button
													key={`contact-list-assistant-message${message?.id}-${j}`}
													onClick={() => {
														onUseContactList(contactListToolCall);
													}}
												>
													Show all {contactListToolCall.totalCount} contacts
												</Button>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
										) {
											const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = socialToolCallPreviewContent(partialSocialCall);
											return (
												<div key={`social-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.content ?? '',
														}}
													/>
													{isSocialToolCall(partialSocialCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseSocialContentHandler(partialSocialCall.content)}
														>
															Use this post
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
										) {
											const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
											return (
												<div
													key={`google-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.reply ?? ''}</div>
													{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
														<Button
															variant='primary'
															styles={[s.useEmailBtn]}
															onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply)}
														>
															Use this Response
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-blog'
										) {
											const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = blogToolCallPreviewContent(partialBlog);
											return (
												<div
													key={`ai-blog-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.title ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isBlogToolCall(partialBlog) ? (
														<Button
															variant='primary'
															styles={[s.useEmailBtn]}
															onClick={() => onUseBlogContentHandler(partialBlog.title, partialBlog.content)}
														>
															Use this blog
														</Button>
													) : null}
												</div>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`email-assistant-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</CampaignComposerAssistantLayout>
	);
}

const defaultOnUseAIEmail =
	(fullscreenModal: FullScreenModalViewModel) => (subject: string, content: Api.IRawRichTextContentState) => {
		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
			state: {
				model: {
					context: {
						subject,
						body: content,
					},
				},
			},
		});
	};
const defaultOnUseSocialContent =
	(fullscreenModal: FullScreenModalViewModel) => (content: Api.IRawRichTextContentState) => {
		fullscreenModal.history.push({
			pathname: `/social-media/post/create`,
			state: {
				initialContent: content,
			},
		});
	};
const defaultOnUseBlogContent =
	(fullscreenModal: FullScreenModalViewModel) => (subject: string, content: Api.IRawRichTextContentState) => {
		fullscreenModal.history.push({
			pathname: `/blog/post/create`,
			state: {
				initialContent: content,
				initialSubject: subject,
			},
		});
	};

const UniversalAssistantChatErrorBoundary = ({ children }: { children: ReactNode }) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SiteWide-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: UNIVERSAL_ASSISTANT_NAME,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function DefaultUniversalAssistantChat(props: Partial<UniversalAssistantChatProps>) {
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	return (
		<UniversalAssistantChatErrorBoundary>
			<UniversalAssistantChat
				onUseAIEmail={defaultOnUseAIEmail(fullscreenModal)}
				onUseSocialContent={defaultOnUseSocialContent(fullscreenModal)}
				/* onUseGoogleReviewReply={_reply => {
			}} */
				onUseBlogContent={defaultOnUseBlogContent(fullscreenModal)}
				onUseContact={contactInfo => {
					history.push(`${ROUTES.contacts.people}/${contactInfo.id}`);
				}}
				onUseContactList={contactList => {
					const searchParams = searchRequestToQueryStringParams(
						{
							filter: { operator: 0, criteria: contactList.filters },
						},
						{ sort: 'asc', sortBy: 'handle' }
					);
					history.push(`${ROUTES.contacts.people}/?search=${searchParams.search}&sort=${searchParams.sort}`);
				}}
				onNavigate={(entityType, entityId, parentEntityType, parentEntityId) => {
					const path = getNavigationPath(
						entityType as NavigationEntityType,
						entityId,
						parentEntityType as NavigationEntityType,
						parentEntityId
					);
					history.push(path);
				}}
				{...props}
			/>
		</UniversalAssistantChatErrorBoundary>
	);
}

interface AdminAppAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	onUseAIEmail?: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState) => void;
	onUseGoogleReviewReply?: (reply: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const AdminAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function AdminAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<AdminAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<AdminAssistantChat {...props} />
		</AdminAssistantChatErrorBoundary>
	);
}

export function AdminAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AdminAppAssistantChat');
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.Admin,
				},
			],
		},
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: ADMIN_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createUniversalAssistantConversation({
				assistantName: ADMIN_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createUniversalAssistantConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, how can I help you with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}?
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === Api.AIChatRole.User) {
							return (
								<AIChatUserMessage key={`email-assistant-message-${message.id ?? i}`}>{message.text}</AIChatUserMessage>
							);
						}
						if (message.role === Api.AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`universal-assistant-message-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as Api.AITextContent;
											return (
												<AIMarkdownResponse key={`email-assistant-message${message?.id}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-email'
										) {
											const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
											return (
												<div key={`email-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div>{previewContent.subject ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isEmailToolCall(partialEmailToolCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseAIEmail(partialEmailToolCall.subject, partialEmailToolCall.body)}
														>
															Use this email
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
										) {
											const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = socialToolCallPreviewContent(partialSocialCall);
											return (
												<div key={`social-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.content ?? '',
														}}
													/>
													{isSocialToolCall(partialSocialCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseSocialContent(partialSocialCall.content)}
														>
															Use this post
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
										) {
											const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
											return (
												<div
													key={`google-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.reply ?? ''}</div>
													{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply)}
														>
															Use this Response
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-blog'
										) {
											const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = blogToolCallPreviewContent(partialBlog);
											return (
												<div
													key={`ai-blog-review-assistant-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.title ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isBlogToolCall(partialBlog) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseBlogContent(partialBlog.title, partialBlog.content)}
														>
															Use this blog
														</Button>
													) : null}
												</div>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`email-assistant-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</UniversalAssistantLayout>
	);
}

const SuccessMeetingPrepAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('SuccessMeetingPrepAssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('SuccessMeetingPrepAssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function SuccessMeetingPrepAppAssistantChat(props: AdminAppAssistantChatProps) {
	return (
		<SuccessMeetingPrepStreamingMutationProvider>
			<SuccessMeetingPrepAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
				<SuccessMeetingPrepAssistantChat {...props} />
			</SuccessMeetingPrepAssistantChatErrorBoundary>
		</SuccessMeetingPrepStreamingMutationProvider>
	);
}

export function SuccessMeetingPrepAssistantChat({
	onUseAIEmail: onUseAIEmailProp,
	onUseSocialContent: onUseSocialContentProp,
	onUseGoogleReviewReply,
	onUseBlogContent: onUseBlogContentProp,
	onClose,
	renderConversationStarters,
	impersonationContext,
}: AdminAppAssistantChatProps) {
	const fullscreenModal = useFullscreenModal();
	const onUseAIEmail = onUseAIEmailProp ?? defaultOnUseAIEmail(fullscreenModal);
	const onUseSocialContent = onUseSocialContentProp ?? defaultOnUseSocialContent(fullscreenModal);
	const onUseBlogContent = onUseBlogContentProp ?? defaultOnUseBlogContent(fullscreenModal);
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SuccessMeetingPrepAssistantChat');
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const aiConversationFilterQuery = useAIConversationFilterQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.SuccessMeetingPrep,
				},
			],
		},
		impersonationContext,
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(aiConversationFilterQuery.data?.length),
		id: aiConversationFilterQuery.data?.[0]?.id,
		staleTime: Infinity,
		impersonationContext,
	});
	const { mutate: createSuccessMeetingPrepConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-SuccessMeetingPrep-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createSuccessMeetingPrepConversation({
			assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
			impersonationContext,
		});
	};
	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;
	useEffect(() => {
		if (aiConversationFilterQuery.isSuccess && !aiConversationFilterQuery.data?.length) {
			createSuccessMeetingPrepConversation({
				assistantName: SUCCESS_MEETING_PREP_AGENT_NAME,
				impersonationContext: impersonationContextRef.current,
			});
		}
	}, [aiConversationFilterQuery.isSuccess, aiConversationFilterQuery.data, createSuccessMeetingPrepConversation]);

	const aiAssistantStreamingMutation = useContext(AIAssistantStreamingMutationContext);
	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest,
			impersonationContext,
		});
	};
	const handlePromptSubmit = (prompt: string) => {
		removeErrorMessageFromConversationCache(conversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};
	if (!aiConversationFilterQuery?.data?.length) {
		return (
			<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
				<LoadingSpinner />
			</UniversalAssistantLayout>
		);
	}
	const conversationId = aiConversationFilterQuery.data[0].id;
	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						Hey {userSession.user.firstName}, I&apos;ll help you prepare for your meeting with{' '}
						{`${accountQuery.data?.companyName ?? 'this account'}`}.
					</AIChatAssistantMessage>
					{renderConversationStarters &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(conversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						if (message.role === Api.AIChatRole.User) {
							return (
								<AIChatUserMessage key={`success-meeting-prep-message-${message.id ?? i}`}>
									{message.text}
								</AIChatUserMessage>
							);
						}
						if (message.role === Api.AIChatRole.Assistant) {
							return (
								<AIChatAssistantMessage key={`success-meeting-prep-assistant-message-${message.id ?? i}`}>
									{message.contents?.map((content, j) => {
										if (content._type === 'AITextContent') {
											const c = content as Api.AITextContent;
											return (
												<AIMarkdownResponse key={`success-meeting-prep-message${message?.id}-${j}`}>
													{c.text}
												</AIMarkdownResponse>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-email'
										) {
											const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
											return (
												<div
													key={`success-meeting-prep-email-message${message?.id}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent.subject ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isEmailToolCall(partialEmailToolCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseAIEmail(partialEmailToolCall.subject, partialEmailToolCall.body)}
														>
															Use this email
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
										) {
											const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = socialToolCallPreviewContent(partialSocialCall);
											return (
												<div
													key={`success-meeting-prep-social-message${message?.id}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.content ?? '',
														}}
													/>
													{isSocialToolCall(partialSocialCall) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseSocialContent(partialSocialCall.content)}
														>
															Use this post
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
										) {
											const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
											return (
												<div
													key={`success-meeting-prep-google-review-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.reply ?? ''}</div>
													{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply)}
														>
															Use this Response
														</Button>
													) : null}
												</div>
											);
										}
										if (
											content._type === 'AIToolContent' &&
											(content as Api.AIToolContent).toolCall.toolName === 'lev-blog'
										) {
											const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
											const previewContent = blogToolCallPreviewContent(partialBlog);
											return (
												<div
													key={`success-meeting-prep-blog-message${message?.id ?? i}-${j}`}
													className={css(s.assistantEmail)}
												>
													<div>{previewContent?.title ?? ''}</div>
													<div
														dangerouslySetInnerHTML={{
															__html: previewContent?.body ?? '',
														}}
													/>
													{isBlogToolCall(partialBlog) ? (
														<Button
															styles={[s.useEmailBtn]}
															onClick={() => onUseBlogContent(partialBlog.title, partialBlog.content)}
														>
															Use this blog
														</Button>
													) : null}
												</div>
											);
										}
									})}
								</AIChatAssistantMessage>
							);
						}
						if ((message as AIChatMessageError).error) {
							const errorMessage = message as AIChatMessageError;
							return (
								<AIChatAssistantMessageError
									key={`success-meeting-prep-error-message-${message.id ?? i}`}
									onTryAgain={() => handleTryAgain(errorMessage.chatRequest)}
								/>
							);
						}
					})}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>
		</UniversalAssistantLayout>
	);
}
